/**
 * Requests API — viewer create-request queue + admin approval queue
 * (Prompt 11E.5, build item 3).
 */

import toast from 'react-hot-toast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiCall, ApiError } from '@core/api/client';

export type RequestType = 'reschedule' | 'membership_cancel' | 'unenroll';
export type RequestStatus = 'pending' | 'approved' | 'denied';

export interface RequestRecord {
  id: string;
  type: RequestType;
  status: RequestStatus;
  student_id: string;
  requested_by_uid: string;
  note?: string | null;
  session_id?: string | null;
  proposed_start?: string | null;
  membership_id?: string | null;
  decided_by_uid?: string | null;
  decided_at?: string | null;
  decision_note?: string | null;
  created_at: string;
  updated_at: string;
}

export interface RequestListResponse {
  items: RequestRecord[];
  total: number;
}

export interface CreateRequestBody {
  type: RequestType;
  student_id: string;
  note?: string;
  session_id?: string;
  proposed_start?: string;
  membership_id?: string;
}

interface DecisionBody {
  note?: string;
}

function toastApiError(err: unknown, fallback: string): void {
  toast.error(err instanceof ApiError ? err.message || fallback : fallback);
}

// ---------------------------------------------------------------------------
// Viewer — create-request-only
// ---------------------------------------------------------------------------

export const requestKeys = {
  mine: ['requests', 'mine'] as const,
  queue: (status?: string, type?: string) => ['requests', 'queue', status, type] as const,
};

export function useMyRequests() {
  return useQuery({
    queryKey: requestKeys.mine,
    queryFn: () => apiCall<RequestListResponse>('GET', '/v1/portal/requests'),
    staleTime: 30_000,
  });
}

export function useCreateRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateRequestBody) =>
      apiCall<RequestRecord>('POST', '/v1/portal/requests', body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: requestKeys.mine });
      toast.success('Request submitted — an admin will review it shortly.');
    },
    onError: (err) => toastApiError(err, 'Failed to submit request'),
  });
}

// ---------------------------------------------------------------------------
// Admin approval queue
// ---------------------------------------------------------------------------

export function useRequestQueue(status?: RequestStatus, type?: RequestType) {
  return useQuery({
    queryKey: requestKeys.queue(status, type),
    queryFn: () =>
      apiCall<RequestListResponse>('GET', '/v1/requests', undefined, {
        query: { status, type },
      }),
    staleTime: 15_000,
  });
}

export function useApproveRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecisionBody }) =>
      apiCall<RequestRecord>('POST', `/v1/requests/${id}/approve`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      toast.success('Request approved');
    },
    onError: (err) => toastApiError(err, 'Failed to approve request'),
  });
}

export function useDenyRequest() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: DecisionBody }) =>
      apiCall<RequestRecord>('POST', `/v1/requests/${id}/deny`, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['requests'] });
      toast.success('Request denied');
    },
    onError: (err) => toastApiError(err, 'Failed to deny request'),
  });
}

